import React, {useEffect, useState} from "react";
import { useEffectOnce } from 'react-use';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CircularProgress from '@mui/material/CircularProgress';
import { Box, Typography, Divider, Avatar, Snackbar } from '@mui/material';
import Modal from '@mui/material/Modal';
import { makeStyles, createStyles } from '@mui/styles';
import MentorCard from "./components/MentorCard";
import { Mentor } from "types";
import { useAuth } from 'app/ProviderAuth';
import {queryMentors} from 'apis/door';
import {AppointmentRequestModal} from './components/AppointmentRequestModal';
import { Button  } from 'components';
const useStyles = makeStyles(() =>
  createStyles({
    titleBackgroud: {
      width: '100%',
      height: 'fit-content',
      backgroundImage: 'url("/DoorTitleBackground.png")',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'center',
    },
    title: {
      gap: "32px",
      margin: "0px auto",
      padding: "80px",
      justifyContent: 'center',
      alignItems: 'center',
      width: "100%",
      maxWidth: "800px",
    },
    subTitleBackground: {
      padding:"48px 80px",
      gap:"24px",
      display:"flex",
      flexDirection:'column',
      background: 'var(--Gradient-background-Linear, linear-gradient(91deg, rgba(198, 245, 213, 0.70) 0%, rgba(159, 228, 254, 0.70) 100%))',
    },
    subTitleStepsBox: {
      display: 'flex',
      justifyContent: 'center',
      flexWrap: 'wrap',
      gridColumnGap: '20px',
      gridRowGap: '32px',
      maxWidth: '1280px',
      margin: '0 auto',
    },
    subTitleStep: {
      display: 'flex',
      gap: '24px',
      width: "240px",
      height: "100px",
      padding: '20px',
      backgroundColor: 'rgba(255, 255, 255, 0.40)',
      backdropFilter: 'blur(20px)',
      borderRadius: "20px",
      justifyContent: 'center',
      alignItems: 'center',
    },
    CardSec:{
      display:'flex',
      flexDirection:'row',
      flexWrap: 'wrap',
      gridColumnGap: '16px',
      gridRowGap: '16px',
      padding: '32px 80px 32px 80px',
      '@media (max-width: 960px)': {padding: '24px 40px 24px 40px',},
      '@media (max-width: 600px)': {padding: '16px 16px 16px 16px',},
      gap: '24px',
      justifyContent:'center',
    }
  })
  )
;
const SLOGAN = `NEX Door 是一個非營利線上導師領航平台，連結世界各地菁英，創造團結互助
                的力量，建立台灣人互助網路在國際路上打開第一扇門或衝刺最後一哩路。\n
                「今日的路人是明日的引路人」，延續 Give and Take 的精神，讓我們啟動正向迴圈，幫助更多台灣人走向世界，讓回家的路變得更好。`;
const STEP1 = `透過篩選國家與諮詢主題，認識海外專業人才。`
const STEP2 = `點選導師簡介，深入了解經歷與可提供的諮詢方向。`
const STEP3 = `登入會員即可送出留言給導師，擴展你的海外人脈！`

export const DoorPage = () => {
    const classes = useStyles();
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const {user} = useAuth();
    const [targetMentor, setTargetMentor] = useState<Mentor|null>(null);
    const [open, setOpen] = useState(false);
    const [snackBarOpen, setSnackBarOpen] = useState(false);
    const [snakerBarMessage, setSnackBarMessage] = useState<string>("成功預約");
    const [mentors, setMentors] = useState<Mentor[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const handleClick = (mentor: Mentor) => {
      setTargetMentor(mentor);
      setOpen(true);
      setSearchParams({mentor: mentor.uid});
    }
    
    useEffectOnce(() => {
      const fetchData = async () => {
        try {
          const mentorsSnapshot = await queryMentors(true);
          const mentor = mentorsSnapshot.docs.map(
            (mentor) => mentor.data() as Mentor
          );
          setMentors(mentor);
        } finally {
          setIsLoading(false);
        }
      };
      fetchData();
    });

    useEffect(() => {
      const mentorUid = searchParams.get('mentor');
      if(mentorUid){
        const mentor = mentors.find((m) => m.uid === mentorUid);
        if(mentor){
          setTargetMentor(mentor);
          setOpen(true);
        }
      }
    }, [searchParams, mentors])

    const closeModal = () => {
      setOpen(false);
      setSnackBarMessage("成功預約");
      setSnackBarOpen(true);
      setSearchParams({});
    }
    const renderMentorCards = () => {
      if(isLoading){
        return <CircularProgress />
      }
      if(mentors.length === 0){
        return <Typography sx={{ fontSize: "18px", marginTop: "32px", color: "text.secondary" }}>目前沒有導師</Typography>;
      }
      return mentors.map((mentor) => {
        return <MentorCard  key={mentor.uid} mentor={mentor} handleClick={handleClick}></MentorCard>
      });
    }
    const handleLogin = () => {
      if (user) {
        setSnackBarMessage("已登入");
        setSnackBarOpen(true);
        return
      }
      navigate('/signin');
    }
    const handleSignUp = () => {
      if (!user) {
        setSnackBarMessage("請先登入");
        setSnackBarOpen(true);
        navigate('/signup');
        return null;
      }
      navigate('/door/signup');
    }
    const onSnapBarClose = () => {
      setSnackBarOpen(false);
    }
    return(
      <Box>
        <Snackbar
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          autoHideDuration={3000}
          onClose={onSnapBarClose}
          open={snackBarOpen}
          message={snakerBarMessage}
        />
        <Modal
          open={open}
          onClose={() => {setOpen(false); setSearchParams({});}}
          aria-labelledby="spring-modal-title"
          aria-describedby="spring-modal-description"
        >
          <AppointmentRequestModal mentor={targetMentor!} user={user} closeModal={closeModal}/>
        </Modal>
        <Box className = {classes.titleBackgroud}>
          <Box sx= {{backdropFilter: 'blur(75px)'}}>
            <Box display="grid" className= {classes.title}>
              <Typography sx={{fontSize: "48px", fontWeight: "bold" }}> Welcome to NEX Door </Typography>
              <Typography sx={{fontSize: "22px", fontWeight: "bold" }}> 串連世界各地的專家，解決各種疑難雜症 </Typography>
              <Divider />
              <Typography sx={{whiteSpace: 'pre-line'}}> {SLOGAN} </Typography>
              <Box sx={{display: 'flex', gap: '40px', justifyContent: 'center', alignItems: 'center'}}>
                <Button shape="round" sx={{width: {xs: "160px", sm: "180px", md: "200px"}}} onClick={handleLogin}>
                  立即登入
                </Button>
                <Button sx={{width: {xs: "160px", sm: "180px", md: "200px"}}} shape="round" onClick={handleSignUp}>
                  快速註冊
                </Button>
            </Box>
            </Box>
          </Box>
          <Box className={classes.subTitleBackground}>
            <Box display="grid" gap={3} sx={{justifyContent: 'center', alignItems: 'center'}}>
              <Typography sx={{fontSize: "22px", fontWeight: "bold" }}> 三步驟幫您擴展海外人脈 </Typography>
            </Box>
            <Box className={classes.subTitleStepsBox}>
              <Box className={classes.subTitleStep}>
                <Avatar sx={{ bgcolor: 'white', color: 'black' }}>1</Avatar>
                <Typography sx={{fontSize: "16px", fontWeight: "500"}}>{STEP1}</Typography>
              </Box>
              <Box className={classes.subTitleStep}>
                <Avatar sx={{ bgcolor: 'white', color: 'black' }}>2</Avatar>
                <Typography sx={{fontSize: "16px", fontWeight: "500"}}>{STEP2}</Typography>
              </Box>
              <Box className={classes.subTitleStep}>
                <Avatar sx={{ bgcolor: 'white', color: 'black' }}>3</Avatar>
                <Typography sx={{fontSize: "16px", fontWeight: "500"}}>{STEP3}</Typography>
              </Box>
            </Box>
          </Box>
        </Box>
        <Box className={classes.CardSec}>
          {renderMentorCards()}
        </Box>

      </Box>
    )
};
